import { create } from "zustand";
import { deleteData, getData, postData, putData } from "../utils/formHelpers";
import { errorAlert, successAlert } from "@app/utils/alerts";
import {
  EfficiencyService,
  IFilterCalibrationEficiency,
} from "@app/types/calibration.types";
import { normalizeDate } from "@app/utils/functions";
import { EFFICIENCY_TYPE } from "@app/pages/calibrations/utils/constants";
import { KeysTabsAnalysis } from "@app/types/samples.types";

function transformPayload(data, idEfficiency) {
  return data.flatMap((item) => [
    {
      id: item.id.startsWith("new") ? undefined : item.id_low,
      isotope: item.isotope,
      a: item.low_energy_TI,
      x: item.low_energy_x,
      x2: item.low_energy_x2,
      x3: item.low_energy_x3,
      x4: item.low_energy_x4,
      efficiency_value: item.low_efficiency,
      is_low: true, // Corrección de is_low_
      efficiency: idEfficiency,
    },
    {
      id: item.id.startsWith("new") ? undefined : item.id_high,
      isotope: item.isotope,
      a: item.high_energy_TI,
      x: item.high_energy_x,
      x2: item.high_energy_x2,
      x3: item.high_energy_x3,
      x4: item.high_energy_x4,
      efficiency_value: item.high_efficiency,
      is_low: false, // Corrección de is_low_
      efficiency: idEfficiency,
    },
  ]);
}

function transformResponse(data) {
  const grouped = {};

  data.forEach((item, index) => {
    const isotopeId = item.isotope ? item.isotope.id : null;

    if (!grouped[isotopeId]) {
      grouped[isotopeId] = {
        id: `edit-${index}`,
        id_low: null,
        id_high: null,
        isotope: isotopeId,
        low_energy_TI: null,
        low_energy_x: null,
        low_energy_x2: null,
        low_energy_x3: null,
        low_energy_x4: null,
        low_efficiency: null,
        high_energy_TI: null,
        high_energy_x: null,
        high_energy_x2: null,
        high_energy_x3: null,
        high_energy_x4: null,
        high_efficiency: null,
      };
    }

    if (item.is_low) {
      grouped[isotopeId].id_low = item.id;
      grouped[isotopeId].low_energy_TI = item.a;
      grouped[isotopeId].low_energy_x = item.x;
      grouped[isotopeId].low_energy_x2 = item.x2;
      grouped[isotopeId].low_energy_x3 = item.x3;
      grouped[isotopeId].low_energy_x4 = item.x4;
      grouped[isotopeId].low_efficiency = item.efficiency_value;
    } else {
      grouped[isotopeId].id_high = item.id;
      grouped[isotopeId].high_energy_TI = item.a;
      grouped[isotopeId].high_energy_x = item.x;
      grouped[isotopeId].high_energy_x2 = item.x2;
      grouped[isotopeId].high_energy_x3 = item.x3;
      grouped[isotopeId].high_energy_x4 = item.x4;
      grouped[isotopeId].high_efficiency = item.efficiency_value;
    }
  });

  return Object.values(grouped);
}

export function filter(
  filters: IFilterCalibrationEficiency,
  efficienciesList: EfficiencyService[]
) {
  const {
    date_to,
    date_from,
    equipment,
    analysis,
    part,
    efficiency_type,
    geometry,
  } = filters;

  const type_efficiency = efficiency_type?.type;

  return efficienciesList.filter((calibration) => {
    const sampleEmissionDate = normalizeDate(new Date(calibration.date));
    const filterDateStart = date_from
      ? normalizeDate(new Date(date_from))
      : null;
    const filterDateEnd = date_to ? normalizeDate(new Date(date_to)) : null;

    const dateInRange =
      (!filterDateStart || sampleEmissionDate >= filterDateStart) &&
      (!filterDateEnd || sampleEmissionDate <= filterDateEnd);

    const equipmentFilter = equipment && equipment.length > 0;
    const equipmentMatch = equipmentFilter
      ? calibration.equipment &&
        equipment.some((e) => e.id === calibration.equipment?.id)
      : true;

    // Adaptar el filtro de análisis para manejar tanto array como objeto único
    const analysisMatch = analysis
      ? Array.isArray(analysis)
        ? calibration.analysis && 
          analysis.some((selectedAnalysis) => 
            calibration.analysis.some((calibrationAnalysis) => 
              calibrationAnalysis.id === selectedAnalysis.id
            )
          )
        : calibration.analysis && 
          calibration.analysis.some((calibrationAnalysis) => 
            calibrationAnalysis.id === analysis.id
          )
      : true;

    const efficiencyTypeMatch = type_efficiency
      ? calibration.efficiency_type &&
        EFFICIENCY_TYPE[calibration.efficiency_type] === type_efficiency
      : true;

    // Manejar part como string simple
    const partMatch = part
      ? (calibration.part === part.parte || calibration.part?.includes(part.parte))
      : true;

    const geometryFilter = geometry && geometry.length > 0;
    const geometryMatch = geometryFilter
      ? calibration.geometry &&
        geometry.some((g) => g.descripcion === calibration.geometry)
      : true;

    return (
      dateInRange &&
      equipmentMatch &&
      analysisMatch &&
      partMatch &&
      efficiencyTypeMatch &&
      geometryMatch
    );
  });
}

type useEfficienciesTypes = {
  efficienciesList: EfficiencyService[];
  get: (id: EfficiencyService["id"]) => EfficiencyService | undefined;
  query: IFilterCalibrationEficiency;
  setQuery: (query: IFilterCalibrationEficiency) => void;
  fetchAll: () => void;
  loading: boolean;
  loadingSave: boolean;
  loadingEfficienciesIsotopes: boolean;
  save: (
    efficiency: Partial<any>,
    idEfficiency?: number,
    codeAnalysis?: string
  ) => Promise<boolean>;
  saveMultiple: (
    efficiencies: Partial<any>[],
    idEfficiency?: number
  ) => Promise<boolean>;
  remove: (id: number) => Promise<void>;
  filteredEfficiencies: () => EfficiencyService[];
  saveEfficienciesIsotopes: (efficienciesIsotopes: any) => Promise<void>;
  fechtEfficienciesIsotopes: (idEfficiency: string) => Promise<any>;
};

export const useEfficiencies = create<useEfficienciesTypes>((set, get) => ({
  loading: false,
  loadingEfficienciesIsotopes: false,
  loadingSave: false,
  efficienciesList: [],
  query: {},
  setQuery: (query) => set({ query }),
  filteredEfficiencies: () => filter(get().query, get().efficienciesList),
  get: (id) =>
    get().efficienciesList.find((efficiencies) => efficiencies.id === id),
  fetchAll: async () => {
    set({ loading: true });
    try {
      const endpoint = "/generic/Efficiencies";
      const { data: response } = await getData(endpoint);

      set(() => ({
        efficienciesList: response.sort(
          (a: EfficiencyService, b: EfficiencyService) => (b.id > a.id ? 1 : -1)
        ),
        loading: false,
      }));
    } catch (error) {
      console.error("Error fetching useEfficiencyParts:", error);
      set({ loading: false });
    }
  },
  save: async (
    efficiency: Partial<any>,
    idEfficiency?: number,
    codeAnalysis?: string
  ) => {
    let hasErrors = false;
    set({ loadingSave: true });

    try {
      const isNew = !idEfficiency;
      const endpoint = `/generic/Efficiencies`;

      const { calibrationIG, ...rest } = efficiency;

      const response = isNew
        ? await postData(endpoint, rest)
        : await putData(`${endpoint}/${idEfficiency}`, rest);
      hasErrors = !response.success;
      if (hasErrors) {
        errorAlert("Error al guardar la Eficiencia");
      } else {
        if (codeAnalysis === KeysTabsAnalysis.IG) {
          const mapping = transformPayload(calibrationIG, response.data.id);
          await get().saveEfficienciesIsotopes(mapping);
        }
        successAlert("Eficiencia guardada correctamente");
      }
      return hasErrors;
    } catch (error) {
      errorAlert("Error al guardar la Eficiencia");
      hasErrors = true;
      return hasErrors;
    } finally {
      set({ loadingSave: false });
      !hasErrors && get().fetchAll();
    }
  },
  saveMultiple: async (efficiencies: Partial<any>[], idEfficiency?: number) => {
    set({ loadingSave: true });
    let hasErrors = false;

    try {
      const isNew = !idEfficiency;
      const endpoint = "/generic/Efficiencies";

      const promises = efficiencies.map(async (efficiency) => {
        if (isNew) {
          return await postData(endpoint, efficiency);
        } else {
          return await putData(`${endpoint}/${idEfficiency}`, efficiency);
        }
      });

      const responses = await Promise.all(promises);

      hasErrors = responses.some((response) => !response.success);

      if (hasErrors) {
        errorAlert("Error al guardar la Eficiencia");
      } else {
        successAlert(efficiencies.length === 1 ? "Eficiencia guardada correctamente" : "Eficiencias guardadas correctamente");
      }

      return hasErrors;
    } catch (error) {
      errorAlert("Error al guardar las Eficiencias");
      return true;
    } finally {
      set({ loadingSave: false });
      if (!hasErrors) {
        get().fetchAll();
      }
    }
  },
  remove: async (id: number) => {
    set({ loading: true });
    try {
      const endpoint = `/generic/Efficiencies/${id}`;
      const response = await deleteData(endpoint);

      if (response.success) {
        successAlert("Eficiencia eliminada correctamente");
      } else {
        errorAlert("Error al eliminar la Eficiencia");
      }
    } catch (error) {
      errorAlert("Error al eliminar la Eficiencia");
    } finally {
      set({ loading: false });
      get().fetchAll();
    }
  },

  saveEfficienciesIsotopes: async (efficienciesIsotopes) => {
    try {
      const endpoint = "/generic/EfficienciesIsotopes";

      const promises = efficienciesIsotopes.map(async (efficiency) => {
        const idEfficiency = efficiency?.id;
        const isNew = !idEfficiency;
        if (isNew) {
          return await postData(endpoint, efficiency);
        } else {
          const { id, ...rest } = efficiency;
          return await putData(`${endpoint}/${idEfficiency}`, rest);
        }
      });

      await Promise.all(promises);
    } catch (error) {
      errorAlert("Error al guardar las Eficiencias");
    }
  },
  fechtEfficienciesIsotopes: async (idEfficiency) => {
    set({ loadingEfficienciesIsotopes: true });
    try {
      const endpoint = `/generic/EfficienciesIsotopes/filtered?efficiency=${idEfficiency}`;
      const { data: response } = await getData(endpoint);

      return transformResponse(response);
    } catch (error) {
      errorAlert("Error al guardar las Eficiencias");
    } finally {
      set({ loadingEfficienciesIsotopes: false });
    }
  },
}));
